import { useRef, useState } from "react";
import { Upload, Loader2, X, Image as ImageIcon, ScanLine, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function fileUrlToFull(url) {
  if (!url) return null;
  if (typeof url !== "string") return null;
  if (url.startsWith("http")) return url;
  if (!url.startsWith("/")) return null;
  return `${BACKEND}${url}`;
}

export default function ImageUpload({ value, onChange, label = "ارفع صورة", testid = "image-upload", accept = "image/*", height = 160 }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("الرجاء اختيار صورة فقط");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("الحجم الأقصى 10 ميجابايت");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(data.url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const clear = () => onChange?.(null);

  return (
    <div data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {value ? (
        <div className="relative inline-block">
          <img
            src={fileUrlToFull(value)}
            alt={label}
            className="rounded-sm border border-border bg-surface object-contain"
            style={{ maxHeight: height, maxWidth: "100%" }}
          />
          <button
            type="button"
            onClick={clear}
            className="absolute -top-2 -start-2 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center shadow-md hover:bg-destructive/90"
            data-testid={`${testid}-clear`}
            title="إزالة"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testid}-button`}
          className="flex items-center justify-center gap-2 px-4 rounded-sm border-2 border-dashed border-border hover:border-primary hover:bg-accent transition-all w-full text-muted-foreground hover:text-primary"
          style={{ height }}
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImageIcon className="w-5 h-5" />}
          <span className="text-sm">{uploading ? "جاري الرفع..." : label}</span>
        </button>
      )}
    </div>
  );
}

export function OcrImageUpload({ value, onChange, testid, ocrPrompt }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("الرجاء اختيار صورة فقط"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("الحجم الأقصى 10 ميجابايت"); return; }
    setUploading(true);
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = ocrPrompt ? { ocr_prompt: ocrPrompt } : {};
      const { data } = await api.post("/upload/ocr", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });
      onChange?.({ url: data.url, name: file.name, type: file.type, ocr_text: data.ocr_text || "" });
      if (data.ocr_text) toast.success("تم استخراج النص بنجاح");
      else toast.info("تم رفع الصورة (لم يتم استخراج نص)");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      setExtracting(false);
    }
  };

  const reRunOcr = async () => {
    if (!value?.url) return;
    setExtracting(true);
    try {
      const params = ocrPrompt ? { ocr_prompt: ocrPrompt } : {};
      const { data } = await api.post("/upload/ocr-text", null, { params: { ...params, url: value.url } });
      onChange?.({ ...value, ocr_text: data.ocr_text || "" });
    } catch {
      // ignore, OCR re-run is best-effort
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {value?.url ? (
        <div className="space-y-3">
          <div className="relative inline-block w-full">
            <img
              src={fileUrlToFull(value.url)}
              alt={value.name || "صورة"}
              className="rounded-sm border border-border max-h-56 object-contain bg-surface w-full"
            />
            <button
              type="button"
              onClick={() => onChange?.(null)}
              className="absolute top-1 end-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center shadow hover:bg-destructive/90"
              title="إزالة"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <ScanLine className="w-3.5 h-3.5" /> النص المستخرج (OCR)
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="text-xs text-primary hover:underline"
                >
                  تغيير الصورة
                </button>
              </div>
            </div>
            {extracting ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-3 px-3 border border-border rounded-sm bg-surface">
                <Loader2 className="w-4 h-4 animate-spin" /> جاري استخراج النص...
              </div>
            ) : (
              <textarea
                className="w-full min-h-[100px] px-3 py-2 text-sm border border-border rounded-sm bg-surface resize-y font-mono"
                value={value.ocr_text || ""}
                onChange={(e) => onChange?.({ ...value, ocr_text: e.target.value })}
                placeholder="النص المستخرج من الصورة سيظهر هنا..."
                dir="auto"
              />
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex flex-col items-center justify-center gap-2 px-4 py-8 rounded-sm border-2 border-dashed border-border hover:border-primary hover:bg-accent transition-all w-full text-muted-foreground hover:text-primary"
        >
          {uploading ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="text-sm">{extracting ? "جاري استخراج النص..." : "جاري الرفع..."}</span>
            </>
          ) : (
            <>
              <ScanLine className="w-6 h-6" />
              <span className="text-sm font-medium">ارفع صورة لاستخراج النص (OCR)</span>
              <span className="text-xs">جواز السفر، الهوية، الوثائق الرسمية</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}

export function FileUploadInline({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("الحجم الأقصى 10 ميجابايت"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.({ url: data.url, name: file.name, type: data.content_type });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const isImage = value?.type?.startsWith("image/");

  return (
    <div data-testid={testid}>
      <input ref={inputRef} type="file" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      {value ? (
        <div className="space-y-2">
          {isImage ? (
            <img src={fileUrlToFull(value.url)} alt={value.name} className="rounded-sm border border-border max-h-48 object-contain bg-surface" />
          ) : (
            <div className="px-3 py-2 rounded-sm border border-border bg-surface text-sm font-mono">{value.name}</div>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
              تغيير
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => onChange?.(null)} className="text-destructive">
              إزالة
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-border bg-card hover:border-primary hover:bg-accent transition-colors w-full text-sm text-muted-foreground hover:text-primary"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "جاري الرفع..." : "اختر ملفاً للرفع"}
        </button>
      )}
    </div>
  );
}
